import { z } from 'zod';

// src/url.ts
var TRACKING_PARAMS = /* @__PURE__ */ new Set([
  // Google / generic campaign tagging
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "utm_name",
  "utm_cid",
  "utm_reader",
  // Ad-network click identifiers
  "gclid",
  "gclsrc",
  "dclid",
  "fbclid",
  "msclkid",
  "twclid",
  "igshid",
  "ttclid",
  // Referral / attribution used by the major job boards
  "ref",
  "referer",
  "referrer",
  "source",
  "src",
  "trk",
  "trkinfo",
  "origin",
  "originalsubdomain",
  "position",
  "pagenum",
  "refid",
  "sharedid",
  "eid",
  "mc_cid",
  "mc_eid",
  "_hsenc",
  "_hsmi",
  "vero_id",
  "vero_conv"
]);
var SCHEME_PATTERN = /^([a-z][a-z0-9+.-]*):(?![0-9])/i;
var InvalidUrlError = class extends Error {
  constructor(input, reason) {
    super(`Invalid bid target URL (${reason}): ${input}`);
    this.input = input;
    this.reason = reason;
    this.name = "InvalidUrlError";
  }
  input;
  reason;
};
function normalizeUrl(input) {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    throw new InvalidUrlError(input, "empty");
  }
  const schemeMatch = SCHEME_PATTERN.exec(trimmed);
  const declaredScheme = schemeMatch?.[1]?.toLowerCase();
  if (declaredScheme !== void 0 && declaredScheme !== "http" && declaredScheme !== "https") {
    throw new InvalidUrlError(input, `unsupported scheme "${declaredScheme}"`);
  }
  const withScheme = declaredScheme !== void 0 ? trimmed : `https://${trimmed}`;
  let url;
  try {
    url = new URL(withScheme);
  } catch {
    throw new InvalidUrlError(input, "unparseable");
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new InvalidUrlError(input, `unsupported scheme "${url.protocol.replace(":", "")}"`);
  }
  if (url.hostname.length === 0) {
    throw new InvalidUrlError(input, "missing host");
  }
  if (url.username.length > 0 || url.password.length > 0) {
    throw new InvalidUrlError(input, "contains embedded credentials");
  }
  const host = url.hostname.startsWith("www.") ? url.hostname.slice(4) : url.hostname;
  const port = url.port ? `:${url.port}` : "";
  const path = url.pathname.length > 1 ? url.pathname.replace(/\/+$/, "") : "";
  const params = [...url.searchParams.entries()].filter(([key]) => !TRACKING_PARAMS.has(key.toLowerCase())).sort(([a, aVal], [b, bVal]) => a === b ? aVal.localeCompare(bVal) : a.localeCompare(b));
  const search = params.length > 0 ? `?${params.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&")}` : "";
  return `https://${host}${port}${path}${search}`;
}
function tryNormalizeUrl(input) {
  try {
    return { ok: true, value: normalizeUrl(input) };
  } catch (error) {
    return { ok: false, reason: error instanceof InvalidUrlError ? error.reason : "unknown" };
  }
}
function isSameTarget(a, b) {
  const left = tryNormalizeUrl(a);
  const right = tryNormalizeUrl(b);
  return left.ok && right.ok && left.value === right.value;
}

// src/profile-completeness.ts
var REQUIREMENTS = ["workHistory", "education"];
function getProfileCompleteness(profile) {
  const missing = REQUIREMENTS.filter((requirement) => profile[requirement].length === 0);
  return { isComplete: missing.length === 0, missing };
}
function isProfileComplete(profile) {
  return getProfileCompleteness(profile).isComplete;
}
var REQUIREMENT_LABELS = {
  workHistory: "work history",
  education: "education history"
};
function describeRequirement(requirement) {
  return REQUIREMENT_LABELS[requirement];
}
function describeIncompleteProfileForBidder(missing) {
  if (missing.length === 0) return "";
  const sections = missing.map(describeRequirement);
  const list = sections.length === 1 ? sections[0] : `${sections.slice(0, -1).join(", ")} and ${sections[sections.length - 1]}`;
  return `This profile has no ${list}. The client needs to add it before you can bid on this profile.`;
}
function describeIncompleteProfileForClient(missing) {
  if (missing.length === 0) return "";
  const sections = missing.map(describeRequirement);
  const list = sections.length === 1 ? sections[0] : `${sections.slice(0, -1).join(", ")} and ${sections[sections.length - 1]}`;
  return `Add ${list} \u2014 bidders cannot generate documents for this profile until you do.`;
}

// src/errors.ts
var ApiErrorCode = {
  /** Credentials rejected, or token expired/invalid. */
  UNAUTHENTICATED: "UNAUTHENTICATED",
  /** Authenticated, but not permitted — including bidders without an active assignment. */
  FORBIDDEN: "FORBIDDEN",
  /** Generic validation failure; `details` carries the Zod issues. */
  VALIDATION_FAILED: "VALIDATION_FAILED",
  /** The bid target URL already exists on this profile (§2.3). */
  DUPLICATE_TARGET: "DUPLICATE_TARGET",
  /** The submitted URL could not be normalized. */
  INVALID_TARGET_URL: "INVALID_TARGET_URL",
  /**
   * Generation blocked: the profile lacks work history and/or education.
   * Carries `missing: ProfileRequirement[]`. Only the *client* can clear this,
   * so bidder-facing UI must say so rather than offering a retry.
   */
  PROFILE_INCOMPLETE: "PROFILE_INCOMPLETE",
  /**
   * A provider returned content referencing records that do not exist on the
   * profile, or skills outside it. This is the anti-fabrication backstop; it
   * means the provider is misbehaving, not the caller.
   */
  GENERATION_VALIDATION_FAILED: "GENERATION_VALIDATION_FAILED",
  /** Per-bidder daily or per-client monthly generation cap reached. */
  GENERATION_CAP_EXCEEDED: "GENERATION_CAP_EXCEEDED",
  /**
   * The desktop build is older than `minSupportedDesktopVersion`. Paired with
   * HTTP 426 and `UpgradeRequiredDetails`; the desktop app intercepts this
   * globally, blocks its UI, and offers the administrator-set download link.
   */
  UPGRADE_REQUIRED: "UPGRADE_REQUIRED",
  /** Invite link is expired, revoked, or already redeemed. */
  INVITATION_INVALID: "INVITATION_INVALID",
  /** Paired with HTTP 429. Retryable after a pause — not a validation problem. */
  RATE_LIMITED: "RATE_LIMITED"
};
var idSchema = z.string().uuid();
var emailSchema = z.string().trim().toLowerCase().email().max(320);
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
var userRoleSchema = z.enum(["ADMIN", "CLIENT", "BIDDER"]);
var bidTargetStatusSchema = z.enum(["OPEN", "COMPLETED", "SKIPPED"]);
var generationProviderSchema = z.enum([
  "mock",
  "anthropic",
  "openai",
  "gemini",
  "deepseek"
]);
var paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50)
});
function paginatedSchema(item) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable()
  });
}
var dateRangeSchema = z.object({
  startDate: isoDateSchema,
  endDate: isoDateSchema.nullable().default(null)
}).refine((v) => v.endDate === null || v.endDate >= v.startDate, {
  message: "endDate must not be before startDate",
  path: ["endDate"]
});
var passwordSchema = z.string().min(12, "Use at least 12 characters").max(200);
var usernameSchema = z.string().trim().min(3).max(50).regex(/^[a-zA-Z0-9._-]+$/, "Letters, numbers, dots, underscores, and hyphens only");
var loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
  /**
   * Bidder accounts are scoped per client, so the same email can exist under
   * more than one. When it does, the caller must say which — the desktop app
   * remembers this after the first successful login.
   */
  clientSlug: z.string().trim().min(1).max(100).optional()
});
var sessionUserSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  clientName: z.string(),
  email: z.string(),
  username: z.string(),
  role: userRoleSchema
});
var loginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: sessionUserSchema
});
var refreshRequestSchema = z.object({ refreshToken: z.string().min(1) });
var createInvitationRequestSchema = z.object({
  /** Fixed at invite time; the bidder cannot change it at signup (§3.1). */
  email: emailSchema,
  // No profileId: it comes from the path (`POST /profiles/:profileId/invitations`).
  // Accepting it in the body too would allow the two to disagree, and the body
  // is the one an attacker controls.
  expiresInDays: z.number().int().min(1).max(30).default(7)
});
var invitationSchema = z.object({
  id: idSchema,
  email: z.string(),
  profileId: idSchema,
  profileName: z.string(),
  /** Present only in the create response — the raw token is never stored or re-shown. */
  inviteUrl: z.string().url().optional(),
  /** Create response only: true when a real mail transport accepted the invite
   *  email. False under the log driver, so the portal keeps saying "copy it". */
  emailSent: z.boolean().optional(),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable()
});
var acceptInvitationRequestSchema = z.object({
  token: z.string().min(1),
  username: usernameSchema,
  password: passwordSchema
});
var invitationPreviewSchema = z.object({
  email: z.string(),
  clientName: z.string(),
  profileName: z.string(),
  expiresAt: z.string().datetime()
});
var desktopDownloadSchema = z.object({
  shareUrl: z.string().url(),
  downloadUrl: z.string().url().nullable(),
  minimumVersion: z.string()
});
var assignedBidderSchema = z.object({
  assignmentId: idSchema,
  userId: idSchema,
  username: z.string(),
  email: z.string(),
  assignedAt: z.string().datetime(),
  bidCount: z.number().int()
});
var assignBidderRequestSchema = z.object({ bidderUserId: idSchema });
var assignmentWarningSchema = z.object({
  isComplete: z.boolean(),
  missing: z.array(z.enum(["workHistory", "education"])),
  message: z.string()
});
var openEndedRange = {
  startDate: isoDateSchema,
  endDate: isoDateSchema.nullable().default(null)
};
var endNotBeforeStart = (v) => v.endDate === null || v.endDate >= v.startDate;
var endNotBeforeStartMessage = {
  message: "End date must not be before the start date",
  path: ["endDate"]
};
var entryId = { id: idSchema.optional() };
var workProjectInputSchema = z.object({
  ...entryId,
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(2e3)
});
var industryInput = z.string().trim().max(80).nullable().optional().transform((value) => value === "" ? null : value);
var workHistoryInputSchema = z.object({
  ...entryId,
  companyName: z.string().trim().min(1).max(200),
  title: z.string().trim().max(200).nullable().default(null),
  industry: industryInput,
  ...openEndedRange,
  projects: z.array(workProjectInputSchema).max(20).default([])
}).refine(endNotBeforeStart, endNotBeforeStartMessage);
var personalProjectInputSchema = z.object({
  ...entryId,
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(2e3),
  ...openEndedRange
}).refine(endNotBeforeStart, endNotBeforeStartMessage);
var educationInputSchema = z.object({
  ...entryId,
  universityName: z.string().trim().min(1).max(200),
  degree: z.string().trim().min(1).max(200),
  ...openEndedRange
}).refine(endNotBeforeStart, endNotBeforeStartMessage);
var profileInputSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  email: z.string().trim().toLowerCase().email().max(320),
  phone: z.string().trim().min(1).max(50),
  location: z.string().trim().min(1).max(200),
  coreSkills: z.array(z.string().trim().min(1).max(80)).min(1).max(60),
  summary: z.string().trim().max(2e3).nullable().default(null),
  /**
   * Per-profile provider override. `null` falls back to the client default, then
   * to the global env default. Per-profile is the finest level, so the two
   * coarser ones come free as fallbacks.
   */
  generationProvider: generationProviderSchema.nullable().default(null),
  workHistory: z.array(workHistoryInputSchema).max(30).default([]),
  personalProjects: z.array(personalProjectInputSchema).max(30).default([]),
  education: z.array(educationInputSchema).max(15).default([])
});
var withId = (shape) => z.object({ id: idSchema, ...shape });
var workProjectSchema = withId({
  name: z.string(),
  description: z.string()
});
var workHistorySchema = withId({
  companyName: z.string(),
  title: z.string().nullable(),
  industry: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  projects: z.array(workProjectSchema)
});
var personalProjectSchema = withId({
  name: z.string(),
  description: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable()
});
var educationSchema = withId({
  universityName: z.string(),
  degree: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable()
});
var profileSchema = withId({
  fullName: z.string(),
  email: z.string(),
  phone: z.string(),
  location: z.string(),
  coreSkills: z.array(z.string()),
  summary: z.string().nullable(),
  generationProvider: generationProviderSchema.nullable(),
  workHistory: z.array(workHistorySchema),
  personalProjects: z.array(personalProjectSchema),
  education: z.array(educationSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var profileSummarySchema = withId({
  fullName: z.string(),
  location: z.string(),
  coreSkills: z.array(z.string()),
  isComplete: z.boolean(),
  missing: z.array(z.enum(["workHistory", "education"])),
  assignedBidderCount: z.number().int(),
  openTargetCount: z.number().int(),
  updatedAt: z.string().datetime()
});
var assignedProfileSchema = withId({
  fullName: z.string(),
  location: z.string(),
  isComplete: z.boolean(),
  missing: z.array(z.enum(["workHistory", "education"])),
  openTargetCount: z.number().int()
});
var generatedWorkEntrySchema = z.object({
  /** Must resolve to a WorkHistory row on this profile. Verified server-side. */
  id: idSchema,
  /** Rewritten achievement bullets. Prose only — no dates, employers, or titles. */
  bullets: z.array(z.string().trim().min(1).max(400)).min(1).max(8)
});
var generatedEducationEntrySchema = z.object({
  /** Must resolve to an Education row on this profile. Verified server-side. */
  id: idSchema
});
var generatedDocsSchema = z.object({
  /** Tailored professional summary. */
  summary: z.string().trim().min(1).max(1200),
  /**
   * The skills section, as resolved in code by `plan.ts`: the profile's declared
   * skills first, then the terms the posting asks for. Verified server-side
   * against that set — a closed list, but no longer a subset of `coreSkills`.
   */
  skills: z.array(z.string().trim().min(1).max(80)).min(1).max(30),
  workHistory: z.array(generatedWorkEntrySchema).min(1).max(20),
  education: z.array(generatedEducationEntrySchema).min(1).max(10),
  coverLetter: z.object({
    paragraphs: z.array(z.string().trim().min(1).max(2e3)).min(1).max(8)
  })
});
var createGenerationRequestSchema = z.object({
  profileId: idSchema,
  /** Optional: links this generation to a target from the client's list. */
  bidTargetId: idSchema.optional(),
  jobDescription: z.string().trim().min(50).max(4e4),
  /** Parsed from the posting by the bidder; used in the cover letter. */
  companyName: z.string().trim().min(1).max(200),
  role: z.string().trim().min(1).max(200)
});
var hydratedWorkEntrySchema = z.object({
  id: idSchema,
  companyName: z.string(),
  title: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  bullets: z.array(z.string())
});
var hydratedEducationEntrySchema = z.object({
  id: idSchema,
  universityName: z.string(),
  degree: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable()
});
var skillGroupSchema = z.object({
  label: z.string(),
  skills: z.array(z.string())
});
var generationResponseSchema = z.object({
  generationId: idSchema,
  provider: generationProviderSchema,
  model: z.string(),
  promptVersion: z.string(),
  /**
   * How much of what the posting emphasised this document actually addresses,
   * 0-100. Null when the posting yielded nothing to score against — a real
   * state (an unstructured or very short posting), not a zero score.
   */
  jdMatchPercent: z.number().int().min(0).max(100).nullable(),
  content: z.object({
    fullName: z.string(),
    email: z.string(),
    phone: z.string(),
    location: z.string(),
    /**
     * The job title for the line under the name. The server derives it from the
     * request's role at hydrate time, never from the provider, and it is null
     * when the role cleans to nothing or runs too long. Optional on the wire,
     * though hydrate always sets it, so a new desktop tolerates an older API.
     */
    headline: z.string().nullable().optional(),
    summary: z.string(),
    skills: z.array(z.string()),
    /**
     * The same skills as labeled rows, each skill once and spelled as in
     * `skills`, Other last. The server groups them at hydrate time from a
     * category table in code, never from the provider, and `skills` stays the
     * flat list. Empty means print `skills` flat. Optional on the wire, though
     * hydrate always sets it, so a new desktop tolerates an older API.
     */
    skillGroups: z.array(skillGroupSchema).optional(),
    workHistory: z.array(hydratedWorkEntrySchema),
    education: z.array(hydratedEducationEntrySchema),
    coverLetter: z.object({
      companyName: z.string(),
      role: z.string(),
      paragraphs: z.array(z.string())
    })
  })
});
var createBidTargetRequestSchema = z.object({
  /** Only the link is required. Normalized server-side for the dedup index. */
  url: z.string().trim().min(1).max(2e3),
  note: z.string().trim().max(500).nullable().default(null)
});
var createBidTargetsBulkRequestSchema = z.object({
  urls: z.array(z.string().trim().min(1).max(2e3)).min(1).max(500)
});
var bulkTargetResultSchema = z.object({
  url: z.string(),
  outcome: z.enum(["created", "duplicate", "invalid"]),
  targetId: idSchema.optional(),
  reason: z.string().optional()
});
var bidTargetSchema = z.object({
  id: idSchema,
  url: z.string(),
  urlNormalized: z.string(),
  status: bidTargetStatusSchema,
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  /** Populated once a bid completes this target — the §2.3 status view. */
  completedByBidderName: z.string().nullable(),
  completedAt: z.string().datetime().nullable(),
  bidId: idSchema.nullable()
});
var uploadedObjectSchema = z.object({
  storageKey: z.string().min(1),
  bytes: z.number().int().positive()
});
var screenshotUploadSchema = uploadedObjectSchema.extend({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  capturedAt: z.string().datetime()
});
var createBidRequestSchema = z.object({
  generationId: idSchema,
  /** Absent for an ad-hoc bid placed outside the client's target list. */
  bidTargetId: idSchema.optional(),
  /** The URL actually applied at — may differ from the target link. */
  finalUrl: z.string().trim().min(1).max(2e3),
  companyName: z.string().trim().min(1).max(200),
  role: z.string().trim().min(1).max(200),
  /**
   * The posting, stored verbatim on the bid.
   *
   * Re-sent rather than carried over from the generation, which stores only a
   * hash of it. The server checks that this text hashes to the generation's
   * `jdHash`, so the job description a client reads is provably the one the
   * documents were written against — not a different posting attached after
   * the fact. Job listings are routinely taken down, so a link would not do.
   */
  jobDescription: z.string().trim().min(50).max(4e4),
  placedAt: z.string().datetime(),
  resumePdf: uploadedObjectSchema,
  coverLetterPdf: uploadedObjectSchema,
  screenshots: z.array(screenshotUploadSchema).max(20).default([])
});
var bidSummarySchema = z.object({
  id: idSchema,
  finalUrl: z.string(),
  companyName: z.string(),
  role: z.string(),
  placedAt: z.string().datetime(),
  screenshotCount: z.number().int(),
  bidderName: z.string(),
  profileName: z.string(),
  /**
   * How much of the posting the submitted documents addressed, 0-100, from the
   * generation this bid was built on. Null for a bid whose generation predates
   * this column, or whose posting yielded nothing to score.
   */
  jdMatchPercent: z.number().int().min(0).max(100).nullable()
});
var bidDetailSchema = bidSummarySchema.extend({
  /**
   * The client's IANA zone, so the detail page renders `placedAt` in the same
   * zone the list grouped and displayed it in. Two views of one bid showing two
   * different times is worse than either choice on its own.
   */
  timezone: z.string(),
  jobDescription: z.string(),
  desktopVersion: z.string().nullable(),
  bidTargetId: idSchema.nullable(),
  /** Short-lived presigned GETs, minted per request — never persisted. */
  resumePdfUrl: z.string().url(),
  coverLetterPdfUrl: z.string().url(),
  screenshots: z.array(
    z.object({
      id: idSchema,
      url: z.string().url(),
      width: z.number().int(),
      height: z.number().int(),
      capturedAt: z.string().datetime()
    })
  )
});
var bidsByDayQuerySchema = z.object({
  profileId: idSchema.optional(),
  bidderUserId: idSchema.optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
});
var bidDayGroupSchema = z.object({
  /** `YYYY-MM-DD` in the client's timezone. */
  day: z.string(),
  /**
   * The IANA zone `day` was computed in.
   *
   * Returned so the UI can render times in the same zone it grouped by.
   * Without it a viewer sees a day header in the client's timezone next to a
   * clock time in their own browser's — the seeded Asia/Seoul tenant shows a
   * bid under 2026-08-10 at "12:56 PM" when it was actually 2:56 AM there.
   */
  timezone: z.string(),
  count: z.number().int(),
  bids: z.array(bidSummarySchema)
});
var logLevelSchema = z.enum(["ERROR", "WARN", "INFO"]);
var logSourceSchema = z.enum(["API", "DESKTOP", "WEB", "ADMIN"]);
var clientLogSourceSchema = z.enum(["DESKTOP", "WEB", "ADMIN"]);
var reportedLogEntrySchema = z.object({
  level: logLevelSchema,
  source: clientLogSourceSchema,
  /** The area that failed: `renderer`, `capture`, `api`, a route name. */
  scope: z.string().trim().max(80).optional(),
  message: z.string().trim().min(1).max(2e3),
  /** An `ApiErrorCode`, an HTTP status, or a client-defined tag. */
  code: z.string().trim().max(120).optional(),
  stack: z.string().max(8e3).optional(),
  /**
   * Free-form detail. Redacted server-side before storage — anything whose key
   * looks like a credential is replaced, because a stack trace or a serialized
   * request is exactly where a token ends up by accident.
   */
  context: z.record(z.unknown()).optional(),
  appVersion: z.string().trim().max(40).optional(),
  /**
   * When it happened on the client, which can predate delivery: reports are
   * queued and flushed, and a network failure is reported once the network
   * comes back.
   */
  occurredAt: z.string().datetime().optional()
});
var reportLogsRequestSchema = z.object({
  entries: z.array(reportedLogEntrySchema).min(1).max(20)
});
var logRecordSchema = z.object({
  id: idSchema,
  createdAt: z.string().datetime(),
  /** Client clock when the entry originated; null for server-side entries. */
  occurredAt: z.string().datetime().nullable(),
  level: logLevelSchema,
  source: logSourceSchema,
  scope: z.string().nullable(),
  message: z.string(),
  code: z.string().nullable(),
  stack: z.string().nullable(),
  context: z.unknown().nullable(),
  appVersion: z.string().nullable(),
  /**
   * Grouping key for "the same defect happening again": derived from source,
   * scope, code, the message with its variable parts masked, and the top stack
   * frame. Stable across occurrences, so the console can collapse a flood into
   * one row and an operator can pull every instance of one issue.
   */
  fingerprint: z.string(),
  /** Null for failures with no tenant: login, boot, background jobs. */
  clientId: idSchema.nullable(),
  clientName: z.string().nullable(),
  userId: idSchema.nullable()
});
var logQuerySchema = z.object({
  level: logLevelSchema.optional(),
  source: logSourceSchema.optional(),
  clientId: idSchema.optional(),
  fingerprint: z.string().max(64).optional(),
  /** Substring match over message, code, and scope. */
  q: z.string().trim().max(200).optional(),
  /** Window, in hours back from now. Absent means everything retained. */
  hours: z.coerce.number().int().positive().max(24 * 90).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50)
});
var logPageSchema = z.object({
  items: z.array(logRecordSchema),
  nextCursor: z.string().nullable()
});
var logIssueSchema = z.object({
  fingerprint: z.string(),
  count: z.number().int(),
  level: logLevelSchema,
  source: logSourceSchema,
  scope: z.string().nullable(),
  message: z.string(),
  code: z.string().nullable(),
  lastSeenAt: z.string().datetime()
});
var logSummarySchema = z.object({
  hours: z.number().int(),
  total: z.number().int(),
  byLevel: z.record(logLevelSchema, z.number().int()),
  bySource: z.record(logSourceSchema, z.number().int()),
  /** Most frequent distinct issues in the window, worst first. */
  topIssues: z.array(logIssueSchema),
  /**
   * Entries the API dropped instead of writing, since boot. Non-zero means the
   * log itself is lossy — a number an operator must be able to see, or a quiet
   * database outage looks like "no errors happened".
   */
  droppedSinceBoot: z.number().int()
});
var purgeLogsRequestSchema = z.object({
  /** 0 clears everything; the endpoint is ADMIN-only and deliberately explicit. */
  olderThanDays: z.coerce.number().int().min(0).max(3650).default(30)
});

export { ApiErrorCode, InvalidUrlError, acceptInvitationRequestSchema, assignBidderRequestSchema, assignedBidderSchema, assignedProfileSchema, assignmentWarningSchema, bidDayGroupSchema, bidDetailSchema, bidSummarySchema, bidTargetSchema, bidTargetStatusSchema, bidsByDayQuerySchema, bulkTargetResultSchema, clientLogSourceSchema, createBidRequestSchema, createBidTargetRequestSchema, createBidTargetsBulkRequestSchema, createGenerationRequestSchema, createInvitationRequestSchema, dateRangeSchema, describeIncompleteProfileForBidder, describeIncompleteProfileForClient, describeRequirement, desktopDownloadSchema, educationInputSchema, educationSchema, emailSchema, generatedDocsSchema, generatedEducationEntrySchema, generatedWorkEntrySchema, generationProviderSchema, generationResponseSchema, getProfileCompleteness, hydratedEducationEntrySchema, hydratedWorkEntrySchema, idSchema, invitationPreviewSchema, invitationSchema, isProfileComplete, isSameTarget, isoDateSchema, logIssueSchema, logLevelSchema, logPageSchema, logQuerySchema, logRecordSchema, logSourceSchema, logSummarySchema, loginRequestSchema, loginResponseSchema, normalizeUrl, paginatedSchema, paginationQuerySchema, passwordSchema, personalProjectInputSchema, personalProjectSchema, profileInputSchema, profileSchema, profileSummarySchema, purgeLogsRequestSchema, refreshRequestSchema, reportLogsRequestSchema, reportedLogEntrySchema, screenshotUploadSchema, sessionUserSchema, skillGroupSchema, tryNormalizeUrl, uploadedObjectSchema, userRoleSchema, usernameSchema, workHistoryInputSchema, workHistorySchema, workProjectInputSchema, workProjectSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map